import { Box, ButtonBase, styled, Typography, useMediaQuery } from "@mui/material";
import AudiotrackIcon from "@mui/icons-material/Audiotrack";
import MovieIcon from "@mui/icons-material/Movie";
import { FileData } from "context/GameContext";
import { isAudio, isPdfFile, isVideo } from "fileBrowser/helpers/fileBrowserHelpers";

import { ReactComponent as FolderIcon } from "./game-filebrowser-folder.svg";
import { ReactComponent as LockIcon } from "./game-filebrowser-lock.svg";
import { getInvertColor } from "utils/themeUtils";

interface Props {
	id?: string;
	selected?: boolean;
	fileData: FileData;
	onClick?: () => void;
	onDoubleClick?: () => void;
	textColor?: string;
}
export default function FileTile(props: Props) {
	const { fileData, onClick, onDoubleClick, textColor, selected, id } = props;

	const isMobile = useMediaQuery("(max-width: 600px)");

	const handleClick = (e: React.MouseEvent) => {
		e.stopPropagation();
		onClick?.();
	};

	const handleDoubleClick = (e: React.MouseEvent) => {
		e.stopPropagation();
		onDoubleClick?.();
	};

	return (
		<Tile
			id={id}
			data-test-id="file-entry"
			onClick={isMobile ? handleDoubleClick : handleClick}
			onDoubleClick={handleDoubleClick}
			title={fileData.name}
			sx={{
				backgroundColor: selected ? "rgba(0,0,0,0.1)" : "transparent",
			}}
		>
			<FileIcon fileData={fileData} />
			<TileText variant="caption" sx={{ color: textColor ?? "#222222" }}>
				{cropFilename(fileData.name)}
			</TileText>
		</Tile>
	);
}

function cropFilename(filename: string, maxLength = 30) {
	const hasExtension = filename.includes(".");
	if (filename.length <= maxLength || !hasExtension) {
		return filename;
	}

	const extIndex = filename.lastIndexOf(".");
	const name = filename.substring(0, extIndex);
	const ext = filename.substring(extIndex);

	const charsToShow = maxLength - 3; // accounting for "..."
	const frontChars = Math.ceil(charsToShow / 2);
	const backChars = Math.floor(charsToShow / 2);

	return `${name.substring(0, frontChars)}...${name.substring(name.length - backChars)}${ext}`;
}

const IconContainer = styled("div")({
	position: "relative",
	height: 60,
	maxWidth: 60,
	width: "100%",
});

const FoldedCornerContainer = styled(IconContainer)({
	background:
		"linear-gradient(to left bottom, transparent 50%, rgba(0,0,0,.4) 0) no-repeat 100% 0 / 0.75em 0.75em, linear-gradient(-135deg, transparent 0.55em, #f5f5f5 0)",
	borderRadius: 1,
	maxWidth: 48,
});

const LockedOverlay = () => {
	return (
		<>
			<Box
				sx={{
					position: "absolute",
					width: "30%",
					height: "100%",
					left: "50%",
					transform: "translateX(-50%)",
					zIndex: 2,
				}}
			>
				<LockIcon style={{ height: "100%", width: "100%" }} />
			</Box>
			<Box
				sx={{
					position: "absolute",
					width: "100%",
					height: "100%",
					zIndex: 1,
					// Darken overlay
					background: "#D2D2D2",
					opacity: 0.9,
					borderRadius: 1,
				}}
			>
				&nbsp;
			</Box>
		</>
	);
};

export const FileIcon = ({ fileData, invertColor = false }: { fileData: FileData; invertColor?: boolean }) => {
	let icon;
	let IContainer = IconContainer;

	if (fileData.isDir) {
		icon = <FolderIcon height="60" width="60" />;
	} else if (isPdfFile(fileData.fileUrl!)) {
		IContainer = FoldedCornerContainer;
		icon = <PDFIcon key={fileData.id} fileData={fileData} />;
	} else if (isAudio(fileData.fileUrl!)) {
		icon = (
			<AudiotrackIcon
				style={{ height: "100%", width: "100%" }}
				sx={(theme) => (invertColor ? { fill: getInvertColor(theme.palette.primary.main) } : {})}
			/>
		);
	} else if (isVideo(fileData.fileUrl!)) {
		icon = (
			<MovieIcon
				style={{ height: "100%", width: "100%" }}
				sx={(theme) => (invertColor ? { fill: getInvertColor(theme.palette.primary.main) } : {})}
			/>
		);
	} else if (fileData.thumbnailUrl) {
		icon = (
			<img
				src={fileData.thumbnailUrl}
				alt={fileData.name}
				style={{
					objectFit: fileData.isDir ? "contain" : "cover",
					width: "100%",
					height: "100%",
					borderRadius: fileData.isDir ? 0 : 4,
				}}
			/>
		);
	}

	return (
		<IContainer>
			{icon}
			{fileData.protection?.locked && <LockedOverlay />}
		</IContainer>
	);
};

const PDFIcon = ({ fileData }: { fileData: FileData }) => {
	const { thumbnailUrl, name } = fileData;
	return (
		<Box sx={{ padding: "0.75rem 0.25rem 0.25rem 0.25rem", position: "absolute", width: "100%" }}>
			{thumbnailUrl && !isPdfFile(thumbnailUrl) ? (
				<Box sx={{ width: "100%", height: "40px", position: "relative", background: "#D9D9D9", flex: 2 }}>
					<img
						src={thumbnailUrl}
						alt={name}
						style={{ objectFit: "contain", width: "100%", height: "100%" }}
					/>
				</Box>
			) : (
				<>
					{/* 5 lines below */}
					<PDFLine />
					<PDFLine />
					<PDFLine />
					<PDFLine />
					<PDFLine />
					<PDFLine />
					<Typography variant="body1" sx={{ fontSize: "0.5rem", margin: "0.0625rem auto", color: "#000" }}>
						PDF
					</Typography>
				</>
			)}
		</Box>
	);
};

const PDFLine = styled("hr")({
	border: 0,
	// borderTop: "1px solid #000",
	height: 1,
	background: "#0000001A",
	width: "100%",
	marginTop: "0.1875rem", //3px
	borderRadius: 8,
	// margin: "0.2px 0",
});

// Styled tile component
export const Tile = styled(ButtonBase)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "flex-start",
	alignItems: "center",
	borderRadius: 8,
	padding: theme.spacing(2),
	// margin: theme.spacing(1),
	cursor: "pointer",
	// height: "180px", // fixed height for uniform tiles
	width: "160px", // fixed width for uniform tiles
	// textAlign: "center",
	transition: "background-color 0.2s",
	"&:hover": {
		backgroundColor: "rgba(0,0,0,0.1)",
	},

	[theme.breakpoints.down("sm")]: {
		width: 120,
		padding: theme.spacing(1),
	},
}));

export const TileText = styled(Typography)({
	marginTop: 8,
	display: "-webkit-box",
	WebkitLineClamp: "2",
	WebkitBoxOrient: "vertical",
	// whiteSpace: "nowrap", // Keep text on a single line
	overflow: "hidden",
	overflowWrap: "break-word",
	textOverflow: "ellipsis", // Add ellipsis for overflowed text
	fontSize: "0.75rem",
	fontWeight: 500,
	width: "100%", // Ensure ellipsis works within the width
});
